import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Trophy, CurrencyInr, Sparkle, Target, ClockCounterClockwise, CheckCircle, XCircle, Plus, ArrowsClockwise, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  approved: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  reversed: "bg-muted text-muted-foreground",
};

const fmt = (n) => (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const thisMonth = () => new Date().toISOString().slice(0, 7);

async function downloadExport(format, params) {
  const { api } = await import("@/lib/api");
  const r = await api.get(`/incentives/export.${format}`, { params, responseType: "blob" });
  const blob = new Blob([r.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `incentives_${params.month || "all"}.${format}`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ExportButtons({ params, testPrefix = "export" }) {
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadExport("csv", params)} data-testid={`${testPrefix}-csv`}>
        <Download size={14} className="mr-1.5" /> CSV
      </Button>
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadExport("xlsx", params)} data-testid={`${testPrefix}-xlsx`}>
        <Download size={14} className="mr-1.5" /> Excel
      </Button>
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadExport("pdf", params)} data-testid={`${testPrefix}-pdf`}>
        <Download size={14} className="mr-1.5" /> PDF
      </Button>
    </div>
  );
}

function StatTile({ label, value, hint, icon: Icon, tone = "default", testId }) {
  const toneMap = {
    default: "bg-card border-border",
    pending: "bg-amber-500/5 border-amber-500/30",
    approved: "bg-sky-500/5 border-sky-500/30",
    paid: "bg-emerald-500/5 border-emerald-500/30",
    total: "bg-primary/10 border-primary/40",
  };
  return (
    <div className={`p-5 rounded-xl border ${toneMap[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline text-muted-foreground">{label}</div>
        {Icon && <Icon size={20} weight="duotone" className="text-muted-foreground" />}
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight">₹{fmt(value)}</div>
      {hint != null && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/* =============================================================================
   Employee / Manager Overview — shown to everyone
   ============================================================================= */
function EmployeeOverview() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [target, setTarget] = useState(null);
  const [status, setStatus] = useState("");

  const load = async () => {
    try {
      const [s, l, t] = await Promise.all([
        api.get("/incentives/summary", { params: { month } }),
        api.get("/incentives", { params: { month, ...(status ? { status_filter: status } : {}) } }),
        api.get(`/targets/${user.id}/${month}`),
      ]);
      setSummary(s.data);
      setLedger(l.data);
      setTarget(t.data);
    } catch (e) { /* soft-fail */ }
  };
  useEffect(() => { load(); }, [month, status]); // eslint-disable-line

  const pct = target?.target ? Math.min(100, target.pct || 0) : 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="overline text-muted-foreground mb-2">My earnings</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">
            ₹{fmt(summary?.total_earned)}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {user.name} · {user.role.replace("_", " ")} · {month}
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-40" data-testid="month-picker" />
          <ExportButtons params={{ month, ...(status ? { status_filter: status } : {}) }} testPrefix="ledger-export" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Pending" value={summary?.pending?.total} hint={`${summary?.pending?.count || 0} record(s)`} icon={ClockCounterClockwise} tone="pending" testId="tile-pending" />
        <StatTile label="Approved" value={summary?.approved?.total} hint={`${summary?.approved?.count || 0} awaiting payout`} icon={CheckCircle} tone="approved" testId="tile-approved" />
        <StatTile label="Paid" value={summary?.paid?.total} hint={`${summary?.paid?.count || 0} completed`} icon={CurrencyInr} tone="paid" testId="tile-paid" />
        <StatTile label="Total earned" value={summary?.total_earned} hint="Pending + Approved + Paid" icon={Sparkle} tone="total" testId="tile-total" />
      </div>

      {target?.target > 0 && (
        <Card className="p-6" data-testid="target-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wider">Monthly Target</div>
              <div className="font-heading text-2xl font-bold mt-1">
                ₹{fmt(target.achieved)} <span className="text-muted-foreground text-lg font-normal">/ ₹{fmt(target.target)}</span>
              </div>
            </div>
            <div className="text-right">
              <Target size={28} weight="duotone" className="text-primary ml-auto" />
              {target.bonus > 0 && <div className="text-xs mt-1 text-muted-foreground">On-hit bonus: ₹{fmt(target.bonus)}</div>}
            </div>
          </div>
          <div className="h-3 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">{pct.toFixed(1)}% achieved</div>
        </Card>
      )}

      <Card className="overflow-hidden" data-testid="ledger-card">
        <div className="p-4 border-b border-border/60 flex items-center justify-between">
          <div className="font-heading text-lg font-bold">Incentive Ledger</div>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8" data-testid="status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                <th className="text-left p-4">Earned</th>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-right p-4">Base</th>
                <th className="text-right p-4">Amount</th>
                <th className="text-left p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((r) => (
                <tr key={r.id} className="border-t border-border/40" data-testid={`ledger-row-${r.id}`}>
                  <td className="p-4 text-xs text-muted-foreground">{(r.earned_at || "").slice(0, 10)}</td>
                  <td className="p-4 font-mono text-xs">{r.order_number || r.order_id?.slice(0, 8)}</td>
                  <td className="p-4">{r.customer_name}</td>
                  <td className="p-4 text-right text-muted-foreground">₹{fmt(r.basis_amount)}</td>
                  <td className="p-4 text-right font-semibold">₹{fmt(r.final_amount)}</td>
                  <td className="p-4"><Badge className={STATUS_BADGE[r.status]}>{r.status}</Badge></td>
                </tr>
              ))}
              {!ledger.length && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No incentives for {month} yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* =============================================================================
   Admin Approvals Queue
   ============================================================================= */
function AdminApprovals() {
  const [month, setMonth] = useState(thisMonth());
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [team, setTeam] = useState([]);
  const [employeeId, setEmployeeId] = useState("");

  const load = async () => {
    const params = { month, status_filter: statusFilter };
    if (employeeId) params.employee_id = employeeId;
    try {
      const [r, u] = await Promise.all([
        api.get("/incentives", { params }),
        team.length ? Promise.resolve({ data: team }) : api.get("/users"),
      ]);
      setRows(r.data);
      if (!team.length) setTeam(u.data);
      setSelected(new Set());
    } catch (e) { /* soft-fail */ }
  };
  useEffect(() => { load(); }, [month, statusFilter, employeeId]); // eslint-disable-line

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const totalSelected = useMemo(() =>
    rows.filter((r) => selected.has(r.id)).reduce((a, r) => a + (r.final_amount || 0), 0),
    [selected, rows]);

  const doBulk = async (kind) => {
    if (!selected.size) return toast.error("Select at least one incentive");
    try {
      const path = kind === "approve" ? "/incentives/approve-bulk" : "/incentives/pay-bulk";
      const body = { incentive_ids: [...selected] };
      if (kind === "pay") body.reference = prompt("Payment reference (UPI/bank/txn id)") || "";
      const r = await api.post(path, body);
      toast.success(`${kind === "approve" ? r.data.approved : r.data.paid} incentive(s) ${kind === "approve" ? "approved" : "paid"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const oneAction = async (id, kind) => {
    try {
      if (kind === "pay") {
        const ref = prompt("Payment reference (UPI/bank/txn id)") || "";
        await api.post(`/incentives/${id}/pay`, null, { params: { reference: ref } });
      } else if (kind === "approve") {
        await api.post(`/incentives/${id}/approve`);
      } else if (kind === "reverse") {
        const reason = prompt("Reason for reversal") || "Manual";
        await api.post(`/incentives/${id}/reverse`, null, { params: { reason } });
      }
      toast.success(`Incentive ${kind}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const userById = Object.fromEntries(team.map((t) => [t.id, t]));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-9 w-40" data-testid="admin-month" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="admin-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Employee</Label>
          <Select value={employeeId || "all"} onValueChange={(v) => setEmployeeId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-52 h-9" data-testid="admin-employee-filter"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <ExportButtons params={{ month, status_filter: statusFilter, ...(employeeId ? { employee_id: employeeId } : {}) }} testPrefix="approvals-export" />
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {selected.size} selected · <span className="font-semibold text-foreground">₹{fmt(totalSelected)}</span>
            </div>
            {statusFilter === "pending" && (
              <Button className="rounded-full" onClick={() => doBulk("approve")} data-testid="bulk-approve"><CheckCircle size={16} className="mr-2" /> Approve</Button>
            )}
            {statusFilter === "approved" && (
              <Button className="rounded-full" onClick={() => doBulk("pay")} data-testid="bulk-pay"><CurrencyInr size={16} className="mr-2" /> Mark paid</Button>
            )}
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                <th className="p-4 w-10">
                  <Checkbox checked={selected.size > 0 && selected.size === rows.length} onCheckedChange={toggleAll} data-testid="approvals-select-all" />
                </th>
                <th className="text-left p-4">Employee</th>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-right p-4">Base</th>
                <th className="text-right p-4">Amount</th>
                <th className="text-left p-4">Earned</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/40 hover:bg-secondary/30" data-testid={`approval-row-${r.id}`}>
                  <td className="p-4">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`approval-check-${r.id}`} />
                  </td>
                  <td className="p-4">
                    <div className="font-medium">{r.employee_name || userById[r.employee_id]?.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{r.employee_role?.replace("_", " ")}</div>
                  </td>
                  <td className="p-4 font-mono text-xs">{r.order_number || r.order_id?.slice(0, 8)}</td>
                  <td className="p-4">{r.customer_name}</td>
                  <td className="p-4 text-right text-muted-foreground">₹{fmt(r.basis_amount)}</td>
                  <td className="p-4 text-right font-semibold">₹{fmt(r.final_amount)}</td>
                  <td className="p-4 text-xs text-muted-foreground">{(r.earned_at || "").slice(0, 10)}</td>
                  <td className="p-4 text-right">
                    {r.status === "pending" && <button onClick={() => oneAction(r.id, "approve")} className="text-xs text-primary hover:underline" data-testid={`approve-${r.id}`}>Approve</button>}
                    {r.status === "approved" && <button onClick={() => oneAction(r.id, "pay")} className="text-xs text-primary hover:underline" data-testid={`pay-${r.id}`}>Pay</button>}
                    {r.status !== "reversed" && r.status !== "paid" && <button onClick={() => oneAction(r.id, "reverse")} className="text-xs text-muted-foreground hover:text-destructive ml-3" data-testid={`reverse-${r.id}`}>Reverse</button>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No {statusFilter} incentives.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* =============================================================================
   Admin — Rules manager
   ============================================================================= */
const BLANK_RULE = {
  name: "", active: true, priority: 10,
  basis: "net_sale", include_tax: false, include_shipping: false,
  include_discount: true, include_refund: true,
  structure: "percentage", rate_pct: 5, fixed_amount: 0,
  applies_to_roles: ["sales_executive"],
  trigger: "full_payment", min_payment_pct: 100,
};

function AdminRules() {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_RULE);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/incentive-rules");
    setRules(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Rule name is required");
    try {
      if (editingId) await api.put(`/incentive-rules/${editingId}`, form);
      else await api.post("/incentive-rules", form);
      toast.success("Rule saved");
      setOpen(false); setForm(BLANK_RULE); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const edit = (r) => { setForm(r); setEditingId(r.id); setOpen(true); };
  const remove = async (id) => {
    if (!confirm("Deactivate this rule?")) return;
    await api.delete(`/incentive-rules/${id}`);
    toast.success("Rule deactivated");
    load();
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfill = async (force) => {
    const msg = force
      ? "Recompute the incentive on EVERY existing order (approved & paid incentives will be skipped). Continue?"
      : "Backfill incentive fields on orders that currently show '—'. Approved/paid orders are skipped. Continue?";
    if (!window.confirm(msg)) return;
    setBackfilling(true);
    try {
      const r = await api.post("/incentives/backfill-orders", null, { params: { force: !!force } });
      const { scanned, updated, cleared, skipped_locked } = r.data;
      toast.success(`Backfill done · scanned ${scanned}, updated ${updated}, cleared ${cleared}, locked ${skipped_locked}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Rules are evaluated in priority order. First match wins.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={() => backfill(false)} disabled={backfilling} data-testid="backfill-orders-button">
            <ArrowsClockwise size={14} className="mr-1.5" />
            {backfilling ? "Backfilling…" : "Backfill missing"}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => backfill(true)} disabled={backfilling} data-testid="backfill-force-button">
            <ArrowsClockwise size={14} className="mr-1.5" />
            Recompute all
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(BLANK_RULE); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-rule-button"><Plus size={16} className="mr-2" /> New rule</Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit rule" : "New incentive rule"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">NAME</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name" />
                </div>
                <div>
                  <Label className="text-xs">PRIORITY</Label>
                  <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: +e.target.value })} data-testid="rule-priority" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">BASIS</Label>
                  <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                    <SelectTrigger data-testid="rule-basis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="net_sale">Net sale (subtotal − discount)</SelectItem>
                      <SelectItem value="gross_sale">Gross sale (incl. GST + shipping)</SelectItem>
                      <SelectItem value="profit_margin">Profit margin</SelectItem>
                      <SelectItem value="fixed_amount">Fixed per sale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">STRUCTURE</Label>
                  <Select value={form.structure} onValueChange={(v) => setForm({ ...form, structure: v })}>
                    <SelectTrigger data-testid="rule-structure"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage of basis</SelectItem>
                      <SelectItem value="fixed">Fixed ₹ per sale</SelectItem>
                      <SelectItem value="slab">Slab (advanced)</SelectItem>
                      <SelectItem value="product_category">Product category (advanced)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.structure === "percentage" && (
                <div>
                  <Label className="text-xs">RATE %</Label>
                  <Input type="number" step="0.01" value={form.rate_pct} onChange={(e) => setForm({ ...form, rate_pct: +e.target.value })} data-testid="rule-rate" />
                </div>
              )}
              {form.structure === "fixed" && (
                <div>
                  <Label className="text-xs">FIXED AMOUNT (₹)</Label>
                  <Input type="number" step="0.01" value={form.fixed_amount} onChange={(e) => setForm({ ...form, fixed_amount: +e.target.value })} data-testid="rule-fixed" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">TRIGGER</Label>
                  <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                    <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_payment">Full payment received</SelectItem>
                      <SelectItem value="partial_payment">Partial payment %</SelectItem>
                      <SelectItem value="order_completed">Order completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.trigger === "partial_payment" && (
                  <div>
                    <Label className="text-xs">MIN PAYMENT %</Label>
                    <Input type="number" step="1" value={form.min_payment_pct} onChange={(e) => setForm({ ...form, min_payment_pct: +e.target.value })} data-testid="rule-min-pct" />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">APPLIES TO ROLES</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["sales_executive", "sales_manager", "admin"].map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                      <Checkbox checked={form.applies_to_roles.includes(r)} onCheckedChange={(v) => {
                        const set = new Set(form.applies_to_roles);
                        v ? set.add(r) : set.delete(r);
                        setForm({ ...form, applies_to_roles: [...set] });
                      }} data-testid={`rule-role-${r}`} />
                      {r.replace("_", " ")}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-4 pt-2 text-sm">
                {[["include_tax", "Include tax"], ["include_shipping", "Include shipping"], ["include_discount", "Deduct discount"], ["include_refund", "Deduct refund"], ["active", "Active"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: !!v })} data-testid={`rule-${k}`} /> {l}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-rule-button">{editingId ? "Save changes" : "Create rule"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Structure</th>
              <th className="text-left p-4">Trigger</th>
              <th className="text-left p-4">Applies to</th>
              <th className="text-right p-4">Priority</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-border/40" data-testid={`rule-row-${r.id}`}>
                <td className="p-4 font-medium">
                  {r.name} {!r.active && <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>}
                </td>
                <td className="p-4 text-xs">
                  {r.structure === "percentage" && <span>{r.rate_pct}% of {r.basis?.replace("_", " ")}</span>}
                  {r.structure === "fixed" && <span>₹{r.fixed_amount} per sale</span>}
                  {r.structure === "slab" && <span>Slab-based</span>}
                  {r.structure === "product_category" && <span>Product category</span>}
                </td>
                <td className="p-4 text-xs capitalize">{r.trigger?.replace("_", " ")}{r.trigger === "partial_payment" && ` (${r.min_payment_pct}%)`}</td>
                <td className="p-4 text-xs">{(r.applies_to_roles || []).map((x) => x.replace("_", " ")).join(", ") || "—"}</td>
                <td className="p-4 text-right text-xs">{r.priority}</td>
                <td className="p-4 text-right space-x-3">
                  <button className="text-xs text-primary hover:underline" onClick={() => edit(r)} data-testid={`edit-rule-${r.id}`}>Edit</button>
                  <button className="text-xs text-muted-foreground hover:text-destructive" onClick={() => remove(r.id)} data-testid={`del-rule-${r.id}`}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No rules yet. Create the first one to start earning incentives.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* =============================================================================
   Admin — Targets
   ============================================================================= */
function AdminTargets() {
  const [team, setTeam] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", month: thisMonth(), target_amount: 0, bonus_amount: 0 });

  const load = async () => {
    const [t, r] = await Promise.all([
      api.get("/users"),
      api.get("/targets", { params: { month } }),
    ]);
    setTeam(t.data);
    setRows(r.data);
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const save = async () => {
    if (!form.employee_id) return toast.error("Pick an employee");
    try {
      await api.post("/targets", { ...form, target_amount: +form.target_amount, bonus_amount: +form.bonus_amount });
      toast.success("Target saved");
      setOpen(false); setForm({ employee_id: "", month, target_amount: 0, bonus_amount: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const userById = Object.fromEntries(team.map((t) => [t.id, t]));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" data-testid="target-month" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="new-target-button"><Target size={16} className="mr-2" /> Set target</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Set monthly target</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">EMPLOYEE</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="target-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                  <SelectContent>
                    {team.filter((t) => t.role === "sales_executive" || t.role === "sales_manager").map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">MONTH</Label>
                <Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="target-form-month" />
              </div>
              <div>
                <Label className="text-xs">TARGET AMOUNT (₹)</Label>
                <Input type="number" step="1000" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} data-testid="target-amount" />
              </div>
              <div>
                <Label className="text-xs">ON-HIT BONUS (₹)</Label>
                <Input type="number" step="500" value={form.bonus_amount} onChange={(e) => setForm({ ...form, bonus_amount: e.target.value })} data-testid="target-bonus" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-target-button">Save target</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="text-left p-4">Employee</th>
              <th className="text-left p-4">Month</th>
              <th className="text-right p-4">Target</th>
              <th className="text-right p-4">Bonus</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border/40" data-testid={`target-row-${r.id}`}>
                <td className="p-4">{userById[r.employee_id]?.name || r.employee_id.slice(0, 8)}</td>
                <td className="p-4 text-xs text-muted-foreground">{r.month}</td>
                <td className="p-4 text-right font-semibold">₹{fmt(r.target_amount)}</td>
                <td className="p-4 text-right">₹{fmt(r.bonus_amount)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="p-10 text-center text-muted-foreground">No targets set for {month}.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* =============================================================================
   Admin — Leaderboard
   ============================================================================= */
function AdminLeaderboard() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/incentives/leaderboard", { params: { month } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [month]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Trophy size={24} weight="duotone" className="text-amber-500" />
          <h2 className="font-heading text-2xl font-bold">Leaderboard</h2>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" data-testid="lb-month" />
      </div>
      <Card className="overflow-hidden" data-testid="leaderboard-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="text-left p-4 w-16">Rank</th>
              <th className="text-left p-4">Employee</th>
              <th className="text-left p-4">Role</th>
              <th className="text-right p-4">Sales</th>
              <th className="text-right p-4">Earned</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.employee_id} className="border-t border-border/40" data-testid={`lb-row-${r.employee_id}`}>
                <td className="p-4 font-heading text-2xl font-bold text-primary">{i + 1}</td>
                <td className="p-4 font-medium">{r.employee_name}</td>
                <td className="p-4 text-xs text-muted-foreground capitalize">{r.employee_role?.replace("_", " ")}</td>
                <td className="p-4 text-right text-muted-foreground">{r.sales_count}</td>
                <td className="p-4 text-right font-semibold text-primary">₹{fmt(r.total_amount)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No approved/paid incentives yet for {month}.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* =============================================================================
   Page — role-aware container
   ============================================================================= */
export default function Incentives() {
  const { user } = useAuth();
  const isAdmin = user?.role === "organization_owner" || user?.role === "admin";

  if (!isAdmin) return <EmployeeOverview />;

  return (
    <div className="space-y-6" data-testid="incentives-admin">
      <div>
        <div className="overline text-muted-foreground mb-2">Compensation</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Incentives</h1>
        <p className="text-sm text-muted-foreground mt-2">Reward the sales floor. Approve, pay out, tune rules, and set monthly targets.</p>
      </div>
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="incentives-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview"><CurrencyInr size={16} className="mr-2" />My Ledger</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals"><CheckCircle size={16} className="mr-2" />Approvals</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy size={16} className="mr-2" />Leaderboard</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules"><Sparkle size={16} className="mr-2" />Rules</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets"><Target size={16} className="mr-2" />Targets</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><EmployeeOverview /></TabsContent>
        <TabsContent value="approvals"><AdminApprovals /></TabsContent>
        <TabsContent value="leaderboard"><AdminLeaderboard /></TabsContent>
        <TabsContent value="rules"><AdminRules /></TabsContent>
        <TabsContent value="targets"><AdminTargets /></TabsContent>
      </Tabs>
    </div>
  );
}
